import React from 'react';
import {
  FaTimes,
  FaUser,
  FaCalendarAlt,
  FaClock,
  FaMoneyBillWave,
  FaCheck,
  FaSpinner,
  FaTruck,
  FaEye,
  FaBuilding,
  FaDoorOpen,
  FaTicketAlt,
  FaShoppingCart,
} from 'react-icons/fa';
import { OrderLog } from '../../services/orderService';

interface OrderDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: OrderLog | null;
  onStatusChange: (orderId: number, newStatus: OrderLog['status']) => void;
}

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({
  isOpen,
  onClose,
  order,
  onStatusChange,
}) => {
  if (!isOpen || !order) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0" onClick={onClose} aria-label="Kapat" />
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 max-w-4xl w-full max-h-[81vh] overflow-hidden flex flex-col relative">
        {/* Header - Fixed */}
        <div className="relative bg-gradient-to-r from-blue-50 to-purple-50 px-6 py-5 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <FaEye className="text-white text-lg" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Sipariş Detayı</h2>
              <p className="text-sm text-gray-600">Sipariş ID: #{order.id}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="absolute right-4 top-4 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/50 transition-colors"
          >
            <FaTimes className="text-gray-500" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Müşteri Bilgileri */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-5 rounded-xl border border-blue-100">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <FaUser className="text-blue-500" />
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Müşteri
                    </span>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">
                    {order.user_name} {order.user_surname}
                  </p>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <FaBuilding className="text-blue-500" />
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Departman
                    </span>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">{order.management}</p>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <FaDoorOpen className="text-blue-500" />
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ofis
                    </span>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">{order.office}</p>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <FaTicketAlt className="text-blue-500" />
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kullanılan Fiş
                    </span>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">{order.ticket_used} fiş</p>
                </div>
              </div>
            </div>

            {/* Sipariş Ürünleri */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-5 rounded-xl border border-green-100">
              <div className="flex items-center gap-2 mb-4">
                <FaShoppingCart className="text-green-500" />
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sipariş Ürünleri
                </span>
              </div>
              <div className="space-y-3">
                {order.items && order.items.length > 0 ? (
                  order.items.map((item, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center bg-white rounded-lg p-4 border border-green-200 shadow-sm"
                    >
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{item.name}</p>
                        {/* Dynamic Options (Yeni Format) */}
                        {item.options && Object.keys(item.options).length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {Object.entries(item.options).map(([key, value], idx) => {
                              // Renk paletleri
                              const colors = [
                                'bg-blue-100 text-blue-700 border-blue-200',
                                'bg-purple-100 text-purple-700 border-purple-200',
                                'bg-green-100 text-green-700 border-green-200',
                                'bg-orange-100 text-orange-700 border-orange-200',
                                'bg-pink-100 text-pink-700 border-pink-200',
                                'bg-indigo-100 text-indigo-700 border-indigo-200',
                              ];

                              // Türkçe label mapping
                              const labelMap: Record<string, string> = {
                                boyut: 'Boyut',
                                dem: 'Dem',
                                seker: 'Şeker',
                                sicaklik: 'Sıcaklık',
                              };

                              // Türkçe value mapping
                              const valueMap: Record<string, string> = {
                                normal: 'Normal',
                                double: 'Büyük',
                                acik: 'Açık',
                                demli: 'Demli',
                                sekersiz: 'Şekersiz',
                                orta: 'Orta',
                                sekerli: 'Şekerli',
                                sicak: 'Sıcak',
                                ilik: 'Ilık',
                                soguk: 'Soğuk',
                              };

                              const displayLabel = labelMap[key] || key;
                              const displayValue = valueMap[value] || value;

                              return (
                                <span
                                  key={key}
                                  className={`px-3 py-1 rounded-full text-xs font-medium border ${colors[idx % colors.length]}`}
                                >
                                  {displayLabel}: {displayValue}
                                </span>
                              );
                            })}
                          </div>
                        )}

                        {/* Backward Compatibility - Eski Format */}
                        {!item.options && (item.size || item.strength) && (
                          <div className="flex gap-2 mt-1">
                            {item.size && (
                              <span className="px-2 py-1 bg-blue-100 text-blue-600 rounded-full text-xs font-medium">
                                Boyut:{' '}
                                {item.size === 'normal'
                                  ? 'Normal'
                                  : item.size === 'double'
                                    ? 'Büyük'
                                    : item.size}
                              </span>
                            )}
                            {item.strength && (
                              <span className="px-2 py-1 bg-purple-100 text-purple-600 rounded-full text-xs font-medium">
                                Dem:{' '}
                                {item.strength === 'light'
                                  ? 'Açık'
                                  : item.strength === 'normal'
                                    ? 'Normal'
                                    : item.strength === 'strong'
                                      ? 'Demli'
                                      : item.strength}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="text-right ml-4">
                        <p className="font-bold text-green-600">{item.quantity} adet</p>
                        <p className="text-sm text-gray-500">{item.price} ₺ / adet</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FaShoppingCart className="mx-auto text-4xl text-gray-300 mb-2" />
                    <p>Ürün bilgisi bulunamadı</p>
                  </div>
                )}
              </div>
            </div>

            {/* Sipariş Özeti */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-5 rounded-xl border border-purple-100">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-3">
                  <FaMoneyBillWave className="text-purple-500 text-lg" />
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">
                      Toplam Tutar
                    </span>
                    <p className="text-2xl font-bold text-green-600">{order.total_amount} ₺</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FaCalendarAlt className="text-purple-500 text-lg" />
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">
                      Sipariş Tarihi
                    </span>
                    <div className="flex items-center gap-2">
                      <p className="text-lg font-semibold text-gray-900">
                        {new Date(order.created_at).toLocaleDateString('tr-TR')}
                      </p>
                      <span className="text-gray-400">•</span>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <FaClock className="text-xs" />
                        {new Date(order.created_at).toLocaleTimeString('tr-TR', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Durum Güncelleme */}
            <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-5 rounded-xl border border-orange-100">
              <div className="flex items-center gap-2 mb-4">
                <FaSpinner className="text-orange-500" />
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sipariş Durumu Güncelle
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                <button
                  onClick={() => onStatusChange(order.id, 'pending')}
                  className={`p-4 rounded-xl text-sm font-medium transition-all duration-200 ${
                    order.status === 'pending'
                      ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white shadow-lg transform scale-105'
                      : 'bg-white border-2 border-yellow-200 text-yellow-700 hover:border-yellow-300 hover:bg-yellow-50'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <FaSpinner
                      className={`text-lg ${order.status === 'pending' ? 'animate-spin' : ''}`}
                    />
                    <span>Bekliyor</span>
                  </div>
                </button>
                <button
                  onClick={() => onStatusChange(order.id, 'preparing')}
                  className={`p-4 rounded-xl text-sm font-medium transition-all duration-200 ${
                    order.status === 'preparing'
                      ? 'bg-gradient-to-r from-blue-400 to-blue-500 text-white shadow-lg transform scale-105'
                      : 'bg-white border-2 border-blue-200 text-blue-700 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <FaSpinner className="text-lg" />
                    <span>Hazırlanıyor</span>
                  </div>
                </button>
                <button
                  onClick={() => onStatusChange(order.id, 'on-the-way')}
                  className={`p-4 rounded-xl text-sm font-medium transition-all duration-200 ${
                    order.status === 'on-the-way'
                      ? 'bg-gradient-to-r from-purple-400 to-purple-500 text-white shadow-lg transform scale-105'
                      : 'bg-white border-2 border-purple-200 text-purple-700 hover:border-purple-300 hover:bg-purple-50'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <FaTruck className="text-lg" />
                    <span>Yolda</span>
                  </div>
                </button>
                <button
                  onClick={() => onStatusChange(order.id, 'completed')}
                  className={`p-4 rounded-xl text-sm font-medium transition-all duration-200 ${
                    order.status === 'completed'
                      ? 'bg-gradient-to-r from-green-400 to-green-500 text-white shadow-lg transform scale-105'
                      : 'bg-white border-2 border-green-200 text-green-700 hover:border-green-300 hover:bg-green-50'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <FaCheck className="text-lg" />
                    <span>Tamamlandı</span>
                  </div>
                </button>
                <button
                  onClick={() => onStatusChange(order.id, 'cancelled')}
                  className={`p-4 rounded-xl text-sm font-medium transition-all duration-200 ${
                    order.status === 'cancelled'
                      ? 'bg-gradient-to-r from-red-400 to-red-500 text-white shadow-lg transform scale-105'
                      : 'bg-white border-2 border-red-200 text-red-700 hover:border-red-300 hover:bg-red-50'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <FaTimes className="text-lg" />
                    <span>İptal Edildi</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailModal;
